import type { QueryClient } from "@tanstack/react-query";
import { del } from "idb-keyval";

import { setAuthorizationToken } from "@/app/api";
import { optOutAnalytics, resetAnalytics } from "@/lib/analytics";

/**
 * Super-admin impersonation session management.
 *
 * Impersonation is a one-way swap: the active bearer is overwritten with the
 * impersonation token and the admin's own token is DISCARDED — never stashed.
 * "Exit" is therefore a full logout (see ImpersonationBanner); the admin signs
 * back in to return to the console. Stashing the admin token was a standing
 * privilege-escalation risk (a recoverable super-admin credential left at rest
 * in localStorage), so the hardened model trades a re-login for holding no
 * admin credential during the impersonated session.
 *
 * `impersonating_email` is the only marker kept — it drives the banner and
 * `isImpersonating`. Every transition full-reloads, so localStorage is the
 * source of truth.
 */
const IMPERSONATING_EMAIL_KEY = "impersonating_email";
// Dispatched on the window whenever the impersonation marker is set or cleared,
// so same-tab subscribers (the banner) react without a full reload — a 401 in
// api.ts clears the marker mid-session with no navigation. The same event name
// is dispatched from api.ts's token cleanup (kept literal there to avoid a
// circular import). Cross-tab changes arrive via the native `storage` event.
export const IMPERSONATION_CHANGE_EVENT = "impersonation-change";
// TanStack Query's persisted cache. It must be dropped on the identity swap,
// exactly like `api.logIn` does: otherwise the post-reload hydration restores
// the PREVIOUS identity's queries — most visibly the cached `user` (a super
// admin has feature_permissions=0), which strands the impersonated session on
// the "access denied" page and defers the offline user-switch wipe until a
// manual refetch.
const REACT_QUERY_PERSIST_KEY = "REACT_QUERY_OFFLINE_CACHE";

async function clearPersistedQueryCache(): Promise<void> {
  try {
    await del(REACT_QUERY_PERSIST_KEY);
  } catch {
    // IndexedDB may be unavailable — the reload still rebuilds in-memory state.
  }
}

export async function startImpersonation(
  token: string,
  email: string,
  queryClient: QueryClient,
): Promise<void> {
  if (typeof globalThis.window === "undefined") return;
  localStorage.setItem(IMPERSONATING_EMAIL_KEY, email);
  globalThis.window.dispatchEvent(new Event(IMPERSONATION_CHANGE_EVENT));
  // Reset the PostHog store before the identity swap so the impersonated
  // session starts from an anonymous store. This is a same-origin navigation
  // (the caller does location.href = "/home"), not a full reload of a fresh
  // origin, so without an explicit reset the admin's distinct_id, `company`
  // group and super-properties would persist and ride the impersonated user's
  // first events + session replay. Safe to call unconditionally — analytics
  // no-ops until PostHog is ready.
  resetAnalytics();
  // Suppress all capture (events + session replay) for the impersonated
  // session so the admin's activity never pollutes the real user's analytics.
  // MUST come AFTER resetAnalytics: reset() clears the persisted opt-out state,
  // so opting out first would be undone. The opt-out is persisted by PostHog
  // and re-applied on the mid-impersonation reload (see PostHogProvider).
  optOutAnalytics();
  // Overwrites the admin's bearer — the admin token is discarded, not stashed.
  setAuthorizationToken(token);
  // Both cache drops come AFTER the token swap, and the in-memory drop comes
  // BEFORE the persisted one. Dropping the persisted cache alone is not enough:
  // the persister is throttled (5 s), and the in-memory client still holds the
  // admin's `user` entry, so a flush scheduled before the `del` can land after
  // it and re-persist the admin identity. On the reload that entry hydrates
  // inside its 60 s staleTime — `getMe` never fires, and the super-admin
  // console renders under the impersonated bearer. Clearing in memory first
  // means any late flush can only write the empty (or already-impersonated)
  // cache. Ordering after the token swap keeps the refetches that `clear()`
  // triggers on the impersonated identity rather than the admin's.
  queryClient.clear();
  await clearPersistedQueryCache();
}

/**
 * Subscribe to impersonation-marker changes. Fires on same-tab set/clear (via
 * the custom event) and cross-tab changes (via the native `storage` event).
 * Returns an unsubscribe function. Used by the banner so it disappears the
 * moment the marker is cleared (logout, 401, re-login) without a reload.
 */
export function subscribeImpersonation(callback: () => void): () => void {
  if (typeof globalThis.window === "undefined") return () => {};
  globalThis.window.addEventListener(IMPERSONATION_CHANGE_EVENT, callback);
  globalThis.window.addEventListener("storage", callback);
  return () => {
    globalThis.window.removeEventListener(IMPERSONATION_CHANGE_EVENT, callback);
    globalThis.window.removeEventListener("storage", callback);
  };
}

export function isImpersonating(): boolean {
  return (
    typeof globalThis.window !== "undefined" &&
    !!localStorage.getItem(IMPERSONATING_EMAIL_KEY)
  );
}

export function getImpersonatedEmail(): string | null {
  if (typeof globalThis.window === "undefined") return null;
  return localStorage.getItem(IMPERSONATING_EMAIL_KEY);
}
